import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from '../Assistant/ordersActions';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTicket, faTimes } from '@fortawesome/free-solid-svg-icons';
import { MyOrder, FullSegment } from 'ts/shared/interfaces/MOResponse';
import { getStatus } from 'constants/mo';
import { getVoucher } from 'services/myOrders';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import MoEmailModal from './MoEmailModal';
import MoVoucherMultiRoom from './MoVoucherMultiRoom';
import useModalClass from 'hooks/useModalClass';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import withErrorBoundary from '../../withErrorBoundary';
import { createPortal } from 'react-dom';
import useDraggableModal from 'hooks/useDraggableModal';
import { MainContext } from 'contexts/MainContext';

interface MoVoucherProps {
  segment: FullSegment;
  myOrder: MyOrder;
  onSuccess: (myOrder: MyOrder) => void;
}

const MoVoucher: FC<MoVoucherProps> = ({ segment, ...props }) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [boVoucher, setBoVoucher] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openMultiRoomModal, setOpenMultiRoomModal] = useState(false);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [roomids, setRoomids] = useState('');
  const [thinReport, setThinReport] = useState('0');
  const [areRoosEnabled, setAreRoosEnabled] = useState(false);
  const { initSiteData } = useContext(MainContext);
  const { activeOrder } = useContext(MoContext);
  const isActive = activeOrder?.fullSegment.orderSegId === segment.orderSegId;

  useEffect(() => {
    setAreRoosEnabled(segment.rooms.some((room) => room.sysStatus.code === 'OK'));
  }, [segment]);

  useEffect(() => {
    if ((initSiteData as any)?.ignoreVoucherNumber === true) {
      setBoVoucher('1');
    } else {
      setBoVoucher('');
    }
    setIsSubmitted(false);
  }, [openModal, initSiteData]);

  const issueVoucher = async (number: string, thin: boolean) => {
    if (!number) return;
    setIsLoading(true);
    await getVoucher(segment, number, thin);
    props.onSuccess({
      ...props.myOrder,
      orderRow: { ...props.myOrder.orderRow, voucher: true },
      fullSegment: { ...props.myOrder.fullSegment, backOfficeExchangeVoucher: number },
    });
    setIsLoading(false);
    setOpenModal(false);
    setIsEmailModalOpen(true);
  };

  const onClickOk = async () => {
    setIsSubmitted(true);
    if (boVoucher) await issueVoucher(boVoucher, thinReport === '1');
  };

  const onClickVoucher = () => {
    const isVoucher = props.myOrder.orderRow.voucher;
    if (!isVoucher) {
      setOpenModal(true);
    } else if (isVoucher && props.myOrder.fullSegment.rooms.length > 1) {
      setOpenMultiRoomModal(true);
    } else if (isVoucher) {
      setIsEmailModalOpen(true);
    }
  };

  useOrdersAction(
    (action) =>
      action.action === 'voucher' &&
      actionTargetsOrder(
        action,
        {
          segmentId: props.myOrder.orderRow.segmentId,
          altSegmentId: segment.orderSegId,
          orderId: props.myOrder.orderRow.orderId,
          altOrderId: segment.orderId,
        },
        isActive,
      ),
    (action) => {
      if (!areRoosEnabled) return;
      if (action.voucherNumber && !props.myOrder.orderRow.voucher) {
        setBoVoucher(action.voucherNumber);
        void issueVoucher(action.voucherNumber, !!action.thin);
        return;
      }
      onClickVoucher();
    },
    isActive,
  );

  const onSuccessMultiRoom = (ids: string, thin: string) => {
    setRoomids(ids);
    setThinReport(thin);
    setIsEmailModalOpen(true);
    setOpenMultiRoomModal(false);
  };

  return (
    <Fragment>
      <span className="mr-5">
        <Button
          onClick={onClickVoucher}
          title={t('mo.voucher_full')}
          className="btn btn-ssm btn-warning"
          icon={faTicket}
          iconclassname="mr-5"
          disabled={!areRoosEnabled}
        />
      </span>

      {openModal && createPortal(
        <div
          className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
          ref={modalOverlayRef}
          onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
        >
          <div ref={modalRef} className={classList} style={modalStyle}>
            <div
              className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
              {...dragHandleProps}
            >
              <h5 className="title">
                {t('mo.print_voucher')}{' '}
                {' '}-{' '}Order: {segment.orderId}{' '}
                Segment:
                {segment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(segment.roomsStatusCode)}`}>
                  {segment.roomsStatusCode}
                </span>
              </h5>
              <div data-no-drag="true">
                <ScreenshotSupport />
                <MaximizeModal modalOverlayRef={modalOverlayRef} />
                <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
              </div>
            </div>

            <div className="modal-body">
              {(initSiteData as any)?.ignoreVoucherNumber !== true && (
                <Fragment>
                  <div className="mb-10">{t('mo.voucher_number')}</div>
                  <FormGroup
                    onChange={(boVoucher) => setBoVoucher(boVoucher)}
                    value={boVoucher}
                    movablePlaceholder={t('mo.backoffice_voucher_identity_number')}
                    label=""
                    isSubmitted={isSubmitted}
                  />
                </Fragment>
              )}

              <div className="mb-20"></div>
              <SelectCustom
                options={[
                  { value: '0', label: t('mo.regular_voucher') },
                  { value: '1', label: t('mo.thin_voucher') },
                ]}
                value={thinReport}
                onChange={(value) => setThinReport(value)}
                movablePlaceholder={t('mo.voucher_type')}
                movablePlaceholderIcon={faTicket}
              />
            </div>
            <div className="modal-footer">
              <Button
                title={t('ok')}
                className="btn btn-primary ml-0 mr-a"
                onClick={onClickOk}
                disabled={isLoading}
              />
              <Button
                title={t('cancel')}
                className="btn btn-default"
                onClick={() => setOpenModal(false)}
              />
            </div>
          </div>
        </div>
        , document.body)}

      {openMultiRoomModal && createPortal(
        <MoVoucherMultiRoom
          isOpen={openMultiRoomModal}
          onClose={() => setOpenMultiRoomModal(false)}
          myOrder={props.myOrder}
          segment={segment}
          onSuccess={onSuccessMultiRoom}
        />
        , document.body)}

      {isEmailModalOpen && createPortal(
        <MoEmailModal
          orderRow={props.myOrder.orderRow}
          onClose={() => setIsEmailModalOpen(false)}
          isOpen={isEmailModalOpen}
          reportType="Voucher"
          roomids={roomids}
          thinVoucher={thinReport}
          showThinVoucher={true}
          segment={segment}
        />
        , document.body)}
    </Fragment>
  );
};

export default withErrorBoundary(MoVoucher, 'MoVoucher');
